/**
 * Copyright Mántum All Rights Reserved.
 */
import { ArrowDownIcon, ArrowLongRightIcon } from '@heroicons/react/24/outline';
import { math } from '@mantum/kanela';
import { useEffect, useRef, useState } from 'react';
import { useGroup, ACTION_VALUE } from '../../group';
import { validator } from '@mantum/kanela';
import NumberField from '../number';
import Trace, { KEY_TRACE } from './trace';

export const KEY_FROZEN = 'frozen';

type PropTypes = {
    alert?: 'success' | 'error' | null;
    resize?: boolean;
    hidden?: boolean;
    max?: number;
    min?: number;
    mode?: 'display' | 'normal';
    name?: string;
    onChange?: (name?: string, value?: unknown) => void;
    mutate?: boolean;
    value?: string | number;
    trace?: string | undefined;
};

export default function Frozen(props: PropTypes) {
    // prettier-ignore
    const {
        name, 
        mutate = false,
        hidden = true
    } = props

    const originalRef = useRef<number>();
    const originalTraceRef = useRef<string | undefined>();

    const groups = useGroup();
    const [value, setValue] = useState<number | undefined>(undefined);
    const [trace, setTrace] = useState<string | undefined>(undefined);

    useEffect(() => {
        let value: string | number | undefined = props.value ?? '';
        if (typeof value === 'string') {
            value = parseFloat(value);
            if (isNaN(value)) {
                value = undefined;
            }
        }

        originalRef.current = value;
        if (!value) {
            setValue(undefined);
        }
    }, [props.value]);

    useEffect(() => {
        const value: string | undefined = props.trace ?? '';
        const current =
            typeof value === 'string' && value !== undefined
                ? value
                : undefined;

        originalTraceRef.current = current;
        if (!current) {
            setTrace(undefined);
        }
    }, [props.trace]);

    useEffect(() => {
        if (!groups) {
            return;
        }

        if (!(KEY_FROZEN in groups)) {
            return;
        }

        const content = groups[KEY_FROZEN];
        if (content.action === ACTION_VALUE) {
            const current =
                typeof content.value === 'string' ||
                typeof content.value === 'number'
                    ? content.value
                    : 0;

            const value =
                !validator.isNull(current) && typeof current === 'number'
                    ? normalize(current, {
                          min: props.min,
                          max: props.max
                      })
                    : Number(current);

            setValue(value);
        }
    }, [groups, props.max, props.min]);

    const onClick = () => {
        const value = originalRef.current;
        const valueTrace = originalTraceRef.current;

        setValue(value);
        setTrace(valueTrace?.trim());

        if (props.onChange) {
            props.onChange(name, value);
            props.onChange(`${name}-manual`, false);
            props.onChange(`${name}-${KEY_TRACE}`, valueTrace);
        }
    };

    const onChange = (_?: string, value?: unknown) => {
        setValue(value as number);

        if (props.onChange) {
            const current = normalize(Number(value), {
                min: props.min,
                max: props.max
            });

            props.onChange(name, current);
        }
    };

    const onKeyDown = () => {
        if (props.onChange) {
            props.onChange(`${name}-manual`, true);
            props.onChange(`${name}-${KEY_TRACE}`, '');
        }
    };

    const onBlur = (event: React.FocusEvent<HTMLInputElement>) => {
        const value = event.target.value;
        if (value) {
            setValue(
                normalize(Number(value), {
                    min: props.min,
                    max: props.max
                })
            );
        }
    };

    const className = mutate
        ? 'absolute top-12 right-[15px] rounded-full p-4'
        : 'rounded p-2';

    return (
        <div className='flex items-center gap-2'>
            <button
                className={`ml-2 cursor-pointer border bg-gray-100 ${className}`}
                data-testid={KEY_FROZEN}
                onClick={onClick}
                type='button'
            >
                {mutate ? (
                    <ArrowDownIcon className='w-5 h-5' />
                ) : (
                    <ArrowLongRightIcon className='w-5 h-5' />
                )}
            </button>
            <>
                <NumberField
                    advanced={{
                        report: true,
                        mode: props.mode,
                        alert: props.alert,
                        mutate: mutate
                    }}
                    onBlur={onBlur}
                    onChange={onChange}
                    onKeyDown={onKeyDown}
                    resize={props.resize}
                    testid={`${name}-${KEY_FROZEN}`}
                    value={value}
                />
                <Trace
                    disabled
                    name={`${name}-${KEY_TRACE}`}
                    onChange={props.onChange}
                    hidden={hidden}
                    value={trace}
                />
            </>
        </div>
    );
}

export const normalize = function <T extends number>(
    value: T,
    between: { min?: number; max?: number }
) {
    if (typeof value === 'number') {
        let extra = math.minBy(value, between.min);
        extra = math.maxBy(extra, between.max);
        return extra;
    }
    return value;
};
